import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invoicesApi } from '../api/invoices';
import { partiesApi } from '../api/parties';
import { companyApi } from '../api/company';
import type { Invoice, InvoiceTrip, Party } from '@coreledger/shared-types';
import { confirm } from '../components/ConfirmDialog';

const today = () => new Date().toISOString().slice(0, 10);
const uid = () => Math.random().toString(36).slice(2);
const f2 = (n: number) => n.toFixed(2);

function emptyTrip(): InvoiceTrip {
  return {
    vehicleNumber: '',
    lrNumber: '',
    lrDate: today(),
    fromCity: '',
    toCity: '',
    driverName: '',
    freightAmount: 0,
    weight: '',
    containerSize: '',
    consignee: '',
    consigneeGstin: '',
    consigneeAddress: '',
  };
}

export default function LorryReceiptFormPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isEdit = !!id;

  const { data: existing, isLoading: loadingInv } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesApi.get(id!),
    enabled: isEdit,
    staleTime: 0,
  });

  const { data: partiesResult } = useQuery({
    queryKey: ['parties'],
    queryFn: () => partiesApi.list(undefined, 1, 200),
    staleTime: 30 * 1000,
  });
  const parties = partiesResult?.data ?? [];

  const { data: sequences = [] } = useQuery({
    queryKey: ['sequences'],
    queryFn: companyApi.getSequences,
    staleTime: 5 * 60 * 1000,
    enabled: !isEdit,
  });

  const nextLrPreview = (() => {
    if (isEdit) return '';
    const seq = sequences.find(s => s.docType === 'LORRY_RECEIPT');
    if (!seq) return 'Auto';
    const yr = new Date().getFullYear();
    const m = new Date().getMonth() + 1;
    const fy = m >= 4 ? `${yr}-${String(yr + 1).slice(2)}` : `${yr - 1}-${String(yr).slice(2)}`;
    return `${seq.prefix}/${fy}/${String(seq.lastNumber + 1).padStart(4, '0')}`;
  })();

  // ── Form state ──────────────────────────────────────────────────────────────
  const [lrNumber, setLrNumber] = useState('');
  const [date, setDate] = useState(today());
  const [partyId, setPartyId] = useState('');
  const [partyName, setPartyName] = useState('');
  const [partyGstin, setPartyGstin] = useState('');
  const [partyAddress, setPartyAddress] = useState('');
  const [consigneeName, setConsigneeName] = useState('');
  const [consigneeGstin, setConsigneeGstin] = useState('');
  const [consigneeAddress, setConsigneeAddress] = useState('');
  const [trips, setTrips] = useState<InvoiceTrip[]>([emptyTrip()]);
  const [freightGstRate, setFreightGstRate] = useState(0);
  const [taxType, setTaxType] = useState<'cgst_sgst' | 'igst'>('cgst_sgst');
  const [isRcm, setIsRcm] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState('');
  const [partySearch, setPartySearch] = useState('');
  const [showPartyDD, setShowPartyDD] = useState(false);
  const [showPartyInline, setShowPartyInline] = useState(false);
  const [inlinePartyForm, setInlinePartyForm] = useState({ name: '', gstin: '', type: 'customer' as 'customer' | 'vendor' | 'both' });
  const [inlinePartySaving, setInlinePartySaving] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const savedRef = useRef(false);
  const formInitialized = useRef(false);

  // ── Load existing LR (edit mode) ────────────────────────────────────────────
  useEffect(() => {
    if (!existing) return;
    setLrNumber(existing.lrNumber ?? existing.invoiceNumber ?? '');
    setDate(existing.date ?? today());
    setPartyId(existing.partyId ?? '');
    setPartyName(existing.partyName ?? '');
    setPartyGstin(existing.partyGstin ?? '');
    setPartyAddress(existing.partyAddress ?? '');
    setConsigneeName(existing.consigneeName ?? '');
    setConsigneeGstin(existing.consigneeGstin ?? '');
    setConsigneeAddress(existing.consigneeAddress ?? '');
    setTrips(existing.trips?.length ? existing.trips : [emptyTrip()]);
    setFreightGstRate(existing.items?.[0]?.gstRate ?? 0);
    setTaxType((existing.taxType as 'cgst_sgst' | 'igst') ?? 'cgst_sgst');
    setIsRcm(existing.isRcm ?? false);
    setTermsAndConditions(existing.termsAndConditions ?? '');
    setPartySearch(existing.partyName ?? '');
  }, [existing]);

  // ── Dirty tracking ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!formInitialized.current) { formInitialized.current = true; return; }
    if (!savedRef.current) setIsDirty(true);
  }, [lrNumber, date, partyId, partyName, trips, freightGstRate, termsAndConditions]);

  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  async function guardedNavigate(to: string) {
    if (isDirty && !savedRef.current && !await confirm({ message: 'You have unsaved changes. Leave anyway?', confirmLabel: 'Leave' })) return;
    navigate(to);
  }

  // ── Computed totals ─────────────────────────────────────────────────────────
  const subtotal = trips.reduce((s, t) => s + (t.freightAmount ?? 0), 0);
  const taxTotal = parseFloat((subtotal * freightGstRate / 100).toFixed(2));
  const total = parseFloat((subtotal + taxTotal).toFixed(2));

  // ── Party dropdown ──────────────────────────────────────────────────────────
  const filteredParties = parties.filter(p => {
    const q = partySearch.toLowerCase();
    return p.name.toLowerCase().includes(q) || (p.gstin ?? '').toLowerCase().includes(q);
  }).slice(0, 10);

  async function handleCreateParty() {
    if (!inlinePartyForm.name.trim()) return;
    setInlinePartySaving(true);
    try {
      const created = await partiesApi.create({ name: inlinePartyForm.name.trim(), gstin: inlinePartyForm.gstin || undefined, type: inlinePartyForm.type });
      selectParty(created as Party);
      setShowPartyInline(false);
      setInlinePartyForm({ name: '', gstin: '', type: 'customer' });
    } catch { alert('Failed to create party'); }
    finally { setInlinePartySaving(false); }
  }

  function selectParty(p: Party) {
    setPartyId(p.id);
    setPartyName(p.name);
    setPartySearch(p.name);
    setPartyGstin(p.gstin ?? '');
    setPartyAddress([p.addressLine1, p.city, p.state].filter(Boolean).join(', '));
    setShowPartyDD(false);
    setIsDirty(true);
  }

  // ── Trip helpers ────────────────────────────────────────────────────────────
  function setTripField(idx: number, k: keyof InvoiceTrip, val: string | number) {
    setTrips(ts => ts.map((t, i) => i === idx ? { ...t, [k]: val } : t));
    setIsDirty(true);
  }

  function addTrip() {
    setTrips(ts => [...ts, emptyTrip()]);
    setIsDirty(true);
  }

  function removeTrip(idx: number) {
    setTrips(ts => ts.filter((_, i) => i !== idx));
    setIsDirty(true);
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (!partyName.trim()) { setError('Consignor (Party) is required'); return; }
    if (!trips.length) { setError('At least one trip is required'); return; }
    setSaving(true);
    setError(null);
    try {
      const freightTotal = subtotal + taxTotal;
      const summaryItem = {
        id: uid(),
        description: 'Freight',
        qty: 1,
        rate: subtotal,
        taxableValue: subtotal,
        gstRate: freightGstRate,
        total: freightTotal,
      };
      const trip0 = trips[0];
      const payload: Partial<Invoice> = {
        docType: 'lorry_receipt',
        lrNumber: lrNumber || undefined,
        invoiceNumber: lrNumber || undefined,
        date,
        partyId: partyId || undefined,
        partyName,
        partyGstin: partyGstin || undefined,
        partyAddress: partyAddress || undefined,
        consigneeName: consigneeName || undefined,
        consigneeGstin: consigneeGstin || undefined,
        consigneeAddress: consigneeAddress || undefined,
        // top-level backward-compat fields from first trip
        vehicleNumber: trip0.vehicleNumber || undefined,
        fromCity: trip0.fromCity || undefined,
        toCity: trip0.toCity || undefined,
        driverName: trip0.driverName || undefined,
        trips,
        taxType,
        isRcm,
        termsAndConditions: termsAndConditions || undefined,
        subtotal,
        taxTotal,
        total,
        lrStatus: (existing?.lrStatus as 'unbilled' | 'billed') ?? 'unbilled',
        items: [summaryItem],
        status: 'unpaid',
      };
      if (isEdit) {
        const updated = await invoicesApi.update(id!, payload);
        qc.invalidateQueries({ queryKey: ['invoices'] });
        qc.refetchQueries({ type: 'all' });
        savedRef.current = true;
        navigate(`/invoices/${updated.id}`);
      } else {
        const created = await invoicesApi.create(payload);
        qc.invalidateQueries({ queryKey: ['invoices'] });
        qc.refetchQueries({ type: 'all' });
        savedRef.current = true;
        navigate(`/invoices/${created.id}`);
      }
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  if (loadingInv) {
    return <div className="page"><div className="loading-overlay"><span className="spinner" /></div></div>;
  }

  return (
    <div className="page">
      {/* Page header */}
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Edit Lorry Receipt' : 'New Lorry Receipt'}</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-ghost" onClick={() => guardedNavigate('/lorry-receipts')}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : (isEdit ? 'Update LR' : 'Save LR')}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>{error}</div>
      )}

      {/* ── LR Number & Date ───────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
          <label>
            LR #{' '}
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
              (blank = auto-generate)
            </span>
            <input
              value={lrNumber}
              onChange={e => setLrNumber(e.target.value)}
              placeholder={nextLrPreview}
            />
          </label>
          <label>
            Date *
            <input type="date" value={date} onChange={e => setDate(e.target.value)} />
          </label>
        </div>
      </div>

      {/* ── Consignor (billing party) ──────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          Consignor (Sender / Billing Party)
        </div>
        <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
          <div style={{ position: 'relative' }}>
            <label>
              Party Name *
              <input
                value={partySearch}
                onChange={e => { setPartySearch(e.target.value); setPartyName(e.target.value); setPartyId(''); setShowPartyDD(true); }}
                onFocus={() => setShowPartyDD(true)}
                onBlur={() => setTimeout(() => setShowPartyDD(false), 180)}
                placeholder="Search or enter party name…"
              />
            </label>
            {showPartyDD && (filteredParties.length > 0 || partySearch.length > 0) && !showPartyInline && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, zIndex: 9999, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto' }}>
                {filteredParties.map(p => (
                  <div key={p.id} onMouseDown={() => selectParty(p)}
                    style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', borderBottom: '1px solid var(--border)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-alt)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface)')}>
                    <div style={{ fontWeight: 600, color: 'var(--text)' }}>{p.name}</div>
                    {p.gstin && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>GSTIN: {p.gstin}</div>}
                    {(p.city || p.state) && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{[p.city, p.state].filter(Boolean).join(', ')}</div>}
                  </div>
                ))}
                <div
                  onMouseDown={() => { setShowPartyInline(true); setShowPartyDD(false); setInlinePartyForm(f => ({ ...f, name: partySearch })); }}
                  style={{ padding: '0.6rem 0.75rem', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.4rem', borderTop: filteredParties.length > 0 ? '1px solid var(--border)' : undefined, background: 'var(--surface-alt)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--primary-light)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'var(--surface-alt)')}>
                  + Create New Party{partySearch ? ` "${partySearch}"` : ''}
                </div>
              </div>
            )}
            {showPartyInline && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '2px solid var(--primary)', borderRadius: 8, zIndex: 9999, boxShadow: '0 8px 24px rgba(0,0,0,0.15)', padding: '1rem' }}>
                <div style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.75rem', color: 'var(--primary)' }}>Quick-Add Party</div>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input value={inlinePartyForm.name} onChange={e => setInlinePartyForm(f => ({ ...f, name: e.target.value }))} placeholder="Party name *" autoFocus style={{ flex: 2 }} />
                  <input value={inlinePartyForm.gstin} onChange={e => setInlinePartyForm(f => ({ ...f, gstin: e.target.value.toUpperCase() }))} placeholder="GSTIN (optional)" maxLength={15} style={{ flex: 1, textTransform: 'uppercase' }} />
                  <select value={inlinePartyForm.type} onChange={e => setInlinePartyForm(f => ({ ...f, type: e.target.value as 'customer' | 'vendor' | 'both' }))} style={{ flex: 1 }}>
                    <option value="customer">Customer</option>
                    <option value="vendor">Vendor</option>
                    <option value="both">Both</option>
                  </select>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-ghost btn-sm" onMouseDown={() => setShowPartyInline(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary btn-sm" disabled={inlinePartySaving || !inlinePartyForm.name.trim()} onMouseDown={handleCreateParty}>
                    {inlinePartySaving ? <span className="spinner" style={{ width: 12, height: 12 }} /> : 'Add Party'}
                  </button>
                </div>
              </div>
            )}
          </div>
          <label>
            GSTIN
            <input value={partyGstin} onChange={e => setPartyGstin(e.target.value)} placeholder="22AAAAA0000A1Z5" />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>
            Address
            <input value={partyAddress} onChange={e => setPartyAddress(e.target.value)} placeholder="City, State" />
          </label>
        </div>
      </div>

      {/* ── Consignee (goods receiver) ─────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          Consignee (Receiver)
        </div>
        <div className="form-row form-row-2" style={{ marginBottom: 0 }}>
          <label>
            Consignee Name
            <input value={consigneeName} onChange={e => setConsigneeName(e.target.value)} placeholder="Receiver / delivery party name" />
          </label>
          <label>
            GSTIN
            <input value={consigneeGstin} onChange={e => setConsigneeGstin(e.target.value)} placeholder="22AAAAA0000A1Z5" />
          </label>
          <label style={{ gridColumn: '1 / -1' }}>
            Delivery Address
            <input value={consigneeAddress} onChange={e => setConsigneeAddress(e.target.value)} placeholder="Delivery address / godown address" />
          </label>
        </div>
      </div>

      {/* ── Trips table ────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
            Trip Details
            {trips.length > 1 && (
              <span style={{ marginLeft: '0.5rem', fontSize: '0.78rem', background: 'var(--primary)', color: '#fff', borderRadius: 99, padding: '1px 8px' }}>
                {trips.length} trips
              </span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={addTrip}>+ Add Trip</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ width: 32 }}>#</th>
                <th>Vehicle No.</th>
                <th>Trip LR #</th>
                <th>Date</th>
                <th>From</th>
                <th>To</th>
                <th>Driver</th>
                <th>Weight</th>
                <th style={{ textAlign: 'right' }}>Freight (₹)</th>
                <th style={{ width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {trips.map((t, i) => (
                <tr key={i}>
                  <td style={{ color: 'var(--text-muted)', fontWeight: 600, textAlign: 'center' }}>{i + 1}</td>
                  <td>
                    <input
                      value={t.vehicleNumber ?? ''}
                      onChange={e => setTripField(i, 'vehicleNumber', e.target.value)}
                      placeholder="MH12AB1234"
                      style={{ minWidth: 110 }}
                    />
                  </td>
                  <td>
                    <input
                      value={t.lrNumber ?? ''}
                      onChange={e => setTripField(i, 'lrNumber', e.target.value)}
                      placeholder="LR-001"
                      style={{ minWidth: 90 }}
                    />
                  </td>
                  <td>
                    <input
                      type="date"
                      value={t.lrDate ?? ''}
                      onChange={e => setTripField(i, 'lrDate', e.target.value)}
                      style={{ minWidth: 130 }}
                    />
                  </td>
                  <td>
                    <input
                      value={t.fromCity ?? ''}
                      onChange={e => setTripField(i, 'fromCity', e.target.value)}
                      placeholder="Mumbai"
                      style={{ minWidth: 95 }}
                    />
                  </td>
                  <td>
                    <input
                      value={t.toCity ?? ''}
                      onChange={e => setTripField(i, 'toCity', e.target.value)}
                      placeholder="Delhi"
                      style={{ minWidth: 95 }}
                    />
                  </td>
                  <td>
                    <input
                      value={t.driverName ?? ''}
                      onChange={e => setTripField(i, 'driverName', e.target.value)}
                      placeholder="Driver name"
                      style={{ minWidth: 110 }}
                    />
                  </td>
                  <td>
                    <input
                      value={t.weight ?? ''}
                      onChange={e => setTripField(i, 'weight', e.target.value)}
                      placeholder="10 MT"
                      style={{ minWidth: 75 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={t.freightAmount || ''}
                      onChange={e => setTripField(i, 'freightAmount', parseFloat(e.target.value) || 0)}
                      placeholder="0"
                      style={{ minWidth: 100, textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    {trips.length > 1 && (
                      <button
                        className="btn btn-danger btn-sm"
                        style={{ padding: '2px 6px', fontSize: '0.85rem' }}
                        onClick={() => removeTrip(i)}
                        title="Remove trip"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── GST on Freight ─────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <div style={{ fontWeight: 700, marginBottom: '1rem', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)' }}>
          GST on Freight (GTA)
        </div>
        <div className="form-row form-row-3" style={{ marginBottom: 0, alignItems: 'end' }}>
          <label>
            GST Rate
            <select value={freightGstRate} onChange={e => setFreightGstRate(parseFloat(e.target.value))}>
              <option value={0}>0% — Exempt / RCM (no GST charged)</option>
              <option value={5}>5% — GTA without ITC</option>
              <option value={18}>18% — GTA with ITC</option>
            </select>
          </label>
          <label>
            Tax Type
            <select value={taxType} onChange={e => setTaxType(e.target.value as 'cgst_sgst' | 'igst')}>
              <option value="cgst_sgst">CGST + SGST (Intra-state)</option>
              <option value="igst">IGST (Inter-state)</option>
            </select>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingBottom: '0.4rem' }}>
            <input
              type="checkbox"
              checked={isRcm}
              onChange={e => setIsRcm(e.target.checked)}
              style={{ width: 'auto', flexShrink: 0 }}
            />
            <span>Reverse Charge (RCM) — receiver pays GST</span>
          </label>
        </div>
      </div>

      {/* ── Notes ──────────────────────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1rem', padding: '1.25rem' }}>
        <label>
          Notes / Remarks
          <textarea
            value={termsAndConditions}
            onChange={e => setTermsAndConditions(e.target.value)}
            rows={2}
            placeholder="Goods description, special instructions, remarks…"
          />
        </label>
      </div>

      {/* ── Totals summary ─────────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.4rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 300 }}>
            <span style={{ color: 'var(--text-muted)' }}>Total Freight ({trips.length} trip{trips.length !== 1 ? 's' : ''})</span>
            <span style={{ fontWeight: 600 }}>₹{f2(subtotal)}</span>
          </div>
          {freightGstRate > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 300 }}>
              <span style={{ color: 'var(--text-muted)' }}>
                GST {freightGstRate}%
                {taxType === 'cgst_sgst'
                  ? ` (CGST ${freightGstRate / 2}% + SGST ${freightGstRate / 2}%)`
                  : ' (IGST)'}
              </span>
              <span>₹{f2(taxTotal)}</span>
            </div>
          )}
          <div style={{
            display: 'flex', justifyContent: 'space-between', width: '100%', maxWidth: 300,
            borderTop: '2px solid var(--border)', paddingTop: '0.5rem', marginTop: '0.25rem',
          }}>
            <span style={{ fontWeight: 800, fontSize: '1rem' }}>Net Total</span>
            <span style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--primary)' }}>₹{f2(total)}</span>
          </div>
        </div>
      </div>

      {/* Bottom action row */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', paddingBottom: '2rem' }}>
        <button className="btn btn-ghost" onClick={() => guardedNavigate('/lorry-receipts')}>Cancel</button>
        <button className="btn btn-primary" disabled={saving} onClick={handleSave}>
          {saving ? <span className="spinner" style={{ width: 16, height: 16 }} /> : (isEdit ? 'Update LR' : 'Save LR')}
        </button>
      </div>
    </div>
  );
}
